/**
 * Filter Persistence System — Core Hook
 *
 * `usePersistentState` is a drop-in replacement for `useState` that
 * automatically persists every value to sessionStorage (or localStorage).
 *
 * Why not `useHistoryState`?
 *   React Router's `history.location.state` is ephemeral: it is scoped
 *   to a single history entry, so a browser refresh — or any navigation
 *   that creates a new entry — silently discards it. It also has a race
 *   condition when multiple hooks call `history.replace` concurrently.
 *
 * Why sessionStorage?
 *   It survives React Router navigation (forward AND back), component
 *   unmount/remount, and F5 refresh — all without any extra configuration.
 *   It is automatically cleared when the user closes the tab, which is
 *   exactly the lifetime we want for filter state.
 *
 * Signature mirrors `useState`:
 *   const [value, setValue] = usePersistentState(namespace, key, defaultValue);
 */

import { useState, useRef, useCallback } from "react";
import { readFromStorage, writeToStorage } from "./storageAdapter";
import { StorageBackend } from "./types";

/**
 * @param namespace  Page-scoped identifier (e.g. "my-inventory").
 *                   All keys for the same page share this prefix so that
 *                   a single `clearNamespace(namespace)` can wipe them all.
 * @param key        Slot name within the namespace (e.g. "filter", "page").
 * @param defaultValue  Fallback when no persisted value exists.
 * @param backend    "session" (default) or "local".
 */
export function usePersistentState<T>(
  namespace: string,
  key: string,
  defaultValue: T,
  backend: StorageBackend = "session"
): readonly [T, (value: T | ((prev: T) => T)) => void] {
  // ── 1. Initialise from storage on first render ────────────────────────────
  //   `useState(() => ...)` (lazy initialiser) ensures the read happens once,
  //   not on every re-render.
  const [state, setStateInternal] = useState<T>(() => {
    const stored = readFromStorage<T>(namespace, key, backend);
    return stored !== undefined ? stored : defaultValue;
  });

  // ── 2. Track the last-written serialised value to skip no-op writes ───────
  //   Comparing JSON strings is sufficient for the small filter objects here.
  //   Using a ref avoids adding the value itself to useCallback's dependency
  //   array, which would recreate the setter on every state change.
  const lastWrittenRef = useRef<string>(JSON.stringify(state));

  // ── 3. Setter: write through to storage, then update React state ──────────
  const setState = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      // Functional form of setStateInternal gives safe access to `prev`
      // without needing it in the dependency array (no stale-closure risk).
      setStateInternal((prev) => {
        const resolved =
          typeof newValue === "function"
            ? (newValue as (p: T) => T)(prev)
            : newValue;

        const serialised = JSON.stringify(resolved);
        if (serialised !== lastWrittenRef.current) {
          lastWrittenRef.current = serialised;
          writeToStorage(namespace, key, resolved, backend);
        }

        return resolved;
      });
    },
    // namespace, key, backend are effectively stable references from the
    // caller's perspective (they should not change after mount).
    [namespace, key, backend]
  );

  return [state, setState] as const;
}
